import React, { useState, useEffect, useCallback } from 'react';
import {
  Steps,
  Card,
  Typography,
  Tag,
  Space,
  Spin,
  Alert,
} from 'antd';
import {
  UserOutlined,
  TruckOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import { customerAPI } from '../services/api';

const { Title, Text } = Typography;
const { Step } = Steps;

const BookingTimeline = ({ bookingId }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingInfo, setBookingInfo] = useState(null);

  const fetchTimeline = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getBookingTimeline(bookingId);

      if (response.success) {
        const timelineData = response.data.timeline;
        const bookingData = {
          bookingNumber: response.data.bookingNumber,
          currentStatus: response.data.currentStatus
        };

        setTimeline(timelineData);
        setBookingInfo(bookingData);
        setError('');
      } else {
        setError(response.message || 'Failed to fetch timeline');
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError('Failed to load booking timeline');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchTimeline();
    }
  }, [bookingId, fetchTimeline]);

  const getIcon = (iconName, status) => {
    const iconMap = {
      'calendar': <CalendarOutlined />,
      'user': <UserOutlined />,
      'truck': <TruckOutlined />,
      'user-check': <UserOutlined />,
      'check-square': <CheckSquareOutlined />,
      'credit-card': <CreditCardOutlined />,
      'check-circle': <CheckSquareOutlined />
    };

    // Default icon style
    let iconStyle = {
      fontSize: '20px',
      color: status === 'completed' ? 'white' : 'gray',  // White color for completed status
      backgroundColor: status === 'completed' ? '#45B140' : '#CCD5F6', // Green background for completed status
      borderRadius: '50%', // Circular background
      marginTop: '5px'
    };

    return (
      <div style={iconStyle}>
        {iconMap[iconName] || <CalendarOutlined />}
      </div>
    );
  };


  const getStepStatus = (event, index) => {
    if (event.status === 'completed') return 'finish';

    if (event.step === 'booking_closed') {
      const vehicleReturnedStep = timeline.find(
        e => e.step === 'pickup_completed' && e.status === 'completed'
      );
      if (vehicleReturnedStep) {
        return 'finish';
      }
    }
    const firstIncompleteIndex = getMainTimelineEvents().findIndex(
      e => e.status !== 'completed'
    );
    if (index === firstIncompleteIndex) {
      return 'process';
    }
    return 'wait';
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return null;
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // const getCurrentStep = () => {
  //   const completedSteps = timeline.filter(event =>
  //     event.status === 'completed' && event.step !== 'payment_event'
  //   );
  //   return completedSteps.length > 0 ? completedSteps.length - 1 : 0;
  // };

  const getCurrentStep = () => {
    const completedSteps = timeline.filter(event =>
      event.status === 'completed' && event.step !== 'payment_event'
    );
    return completedSteps.length;
  };

  const getMainTimelineEvents = () => {
    return timeline.filter(event => event.step !== 'payment_event');
  };

  const getPaymentEvents = () => {
    return timeline.filter(event => event.step === 'payment_event');
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading timeline...</Text>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  return (
    <div><style>
      {`
      .blinking-icon {
        animation: blink 1s infinite;
      }
      @keyframes blink {
        0% { opacity: 1; }
        50% { opacity: 0.3; }
        100% { opacity: 1; }
      }
    `}
    </style>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={4} style={{ marginBottom: 8 }}>
            Booking Timeline
          </Title>
          {bookingInfo && (
            <Text type="secondary">
              {bookingInfo.bookingNumber} • Current Status: <strong>{bookingInfo.currentStatus}</strong>
            </Text>
          )}
        </div>

        <Steps
          direction="vertical"
          current={getCurrentStep()}
          style={{ marginBottom: 24 }}
        >
          {getMainTimelineEvents().map((event, index) => (
            <Step
              key={`${event.step}-${index}`}
              title={event.title}
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    {event.description}
                  </div>
                  {event.timestamp && (
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: 4 }}>
                      {formatDateTime(event.timestamp)}
                    </div>
                  )}
                  {event.assignedTo && (
                    <div style={{ fontSize: '12px', color: '#1890ff' }}>
                      Assigned to: {event.assignedTo.name} {event.assignedTo.lastName}
                    </div>
                  )}
                  {event.paymentData && (
                    <div style={{ marginTop: 8 }}>
                      <Tag color="orange" style={{ marginBottom: 4 }}>
                        {event.paymentData.previousStatus} → {event.paymentData.newStatus}
                      </Tag>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Amount: {event.paymentData.amount} • By: {event.paymentData.updatedBy}
                      </div>
                    </div>
                  )}
                </div>
              }
            
              status={getStepStatus(event, index)}
           
              icon={
                getStepStatus(event, index) === 'process'
                  ? <span className="blinking-icon">{getIcon(event.icon, event.status)}</span>
                  : getIcon(event.icon, getStepStatus(event, index) === 'finish' ? 'completed' : event.status)
              }
            />
          ))}
        </Steps>

        {/* Payment Events */}
        {getPaymentEvents().length > 0 && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Title level={5} style={{ marginBottom: 12 }}>
              Payment History
            </Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {getPaymentEvents().map((event, index) => (
                <div key={`payment-${index}`} style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff7e6',
                  borderRadius: '6px',
                  border: '1px solid #ffd591'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CreditCardOutlined style={{ color: '#fa8c16' }} />
                    <Text strong>{event.title}</Text>
                    <Tag color="orange" size="small">
                      {event.paymentData.newStatus}
                    </Tag>
                  </div>
                  <div style={{ marginTop: 4, fontSize: '12px', color: '#8c8c8c' }}>
                    {formatDateTime(event.timestamp)} • Amount: {event.paymentData.amount}
                  </div>
                </div>
              ))}
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BookingTimeline;